/**
 * Pages piliers du site, créées par le seed et marquées `is_system`.
 *
 * Chacune correspond à une entrée du menu d'administration ou à une obligation
 * légale. Leur slug est figé : il fait le lien avec le menu (`/pages/<slug>`) et
 * avec les routes publiques. Elles ne sont ni supprimables ni renommables, et
 * n'apparaissent pas dans la liste des pages — leur titre et leur contenu, eux,
 * restent modifiables.
 *
 * L'ordre de ce tableau reprend celui du menu ; il alimente `sortOrder`.
 */
export interface SystemPageDefinition {
  slug: string
  title: string
  /** Sert d'amorce à la rédaction et de description par défaut pour le référencement. */
  excerpt: string
  /**
   * Contenu de départ, pour les pages dont le site attend un bloc natif.
   *
   * Neuf rubriques ne montrent leur bloc — mosaïque des thèmes, formulaire de
   * contact, liste d'articles, d'actualités, de partenaires, d'ateliers, de
   * lettres, de documents, de décisions — que si la brique `:::bloc` figure
   * dans leur contenu : c'est le rédacteur qui décide où la poser. Sans
   * amorce, une installation neuve afficherait une rubrique vide jusqu'à ce
   * que quelqu'un devine qu'il manque la brique — et la disparition est
   * silencieuse, le message « Aucun élément publié » étant lui-même à
   * l'intérieur du bloc.
   *
   * Les pages qui se suffisent — « Qui sommes-nous », les mentions légales —
   * n'ont pas de bloc natif et n'en attendent donc aucune amorce.
   *
   * Posé à la création seulement : le seed n'écrase jamais un contenu existant.
   */
  content?: string
  /** Réglage de l'écran d'édition, voir `EditionPage`. Absent : écran complet. */
  edition?: EditionPage
}

/**
 * Parties de l'écran d'édition qu'une page système peut masquer.
 *
 * - `contenu` : l'éditeur du corps Markdown ;
 * - `entete` : titre, description, icône et couleur du bandeau ;
 * - `datePublication` : la date de publication modifiable ;
 * - `dateMiseAJour` : l'option « afficher la date de mise à jour » ;
 * - `tempsLecture` : l'option « afficher la durée de lecture ».
 */
export type PartieEcranPage = 'contenu' | 'entete' | 'datePublication' | 'dateMiseAJour' | 'tempsLecture'

/**
 * Réglage déclaratif de l'écran d'édition d'une page système.
 *
 * **Masquer n'est pas effacer** : la valeur en base ne change pas, elle n'est
 * simplement plus proposée à l'écran. Le formulaire la renvoie telle qu'il l'a
 * lue, si bien qu'un enregistrement ne l'écrase pas.
 */
export interface EditionPage {
  /** Parties de l'écran à ne pas afficher. */
  masquer?: PartieEcranPage[]
  /**
   * Champs personnalisés placés avant l'extrait, à la place du contenu —
   * pour une page dont ils sont l'essentiel. Sinon, après l'extrait.
   */
  champsAvantExtrait?: boolean
}

export const systemPages: SystemPageDefinition[] = [
  // ── Site ──
  {
    // Ses sections (carrousel, association, chiffres, apports, actualité,
    // cibles, FNACC, témoignages) se règlent dans les champs personnalisés
    // `accueil-*` de son écran (`custom-fields.ts`) ; le bandeau « Adhérer »
    // est son bandeau de pied. Le corps Markdown n'est pas affiché par le site.
    slug: 'accueil',
    title: 'Page d\'accueil',
    excerpt: 'Page vitrine principale du site de l\'ACC Sud-Ouest.',
    // Le site ne rend ni son corps ni son bandeau de titre : ses sections sont
    // ses champs personnalisés, présentés à la place du contenu.
    edition: {
      masquer: ['contenu', 'entete', 'datePublication', 'dateMiseAJour', 'tempsLecture'],
      champsAvantExtrait: true,
    },
  },
  {
    slug: 'qui-sommes-nous',
    title: 'Qui sommes-nous',
    excerpt: 'Présentation de l\'ACC Sud-Ouest, de son histoire et de ses missions.',
  },
  {
    slug: 'fnacc',
    title: 'FNACC',
    excerpt: 'La Fédération Nationale des Associations de Copropriétaires et son rôle auprès de l\'ACC Sud-Ouest.',
  },
  {
    slug: 'articles',
    title: 'Articles',
    excerpt: 'Les articles de fond publiés par l\'ACC Sud-Ouest.',
    content: 'Nos **articles de fond** décryptent les sujets qui reviennent le plus souvent en copropriété : charges, travaux, assemblée générale, relations avec le syndic.\n\n:::bloc\n:::\n',
  },
  {
    slug: 'actualites',
    title: 'Actualités',
    excerpt: 'Les actualités de l\'association et de la copropriété.',
    content: 'Retrouvez ici l\'ensemble des **actualités** de l\'ACC Sud-Ouest : vie de l\'association, évolutions réglementaires et informations utiles aux copropriétaires.\n\n:::bloc\n:::\n',
  },
  {
    slug: 'legislation',
    title: 'Législation',
    excerpt: 'Les textes et thèmes législatifs applicables à la copropriété.',
    // La mosaïque des thèmes ne paraît qu'où la brique est posée. Elle vient
    // ici après le texte d'introduction, comme sur la maquette.
    content: 'La copropriété est un cadre juridique complexe, qui varie selon la forme de l\'organisation et le type d\'immeuble concerné. Chaque configuration implique des règles spécifiques.\n\nPour faciliter la compréhension, la législation applicable est présentée ci-dessous par grandes thématiques.\n\n:::bloc\n:::',
  },
  {
    slug: 'jurisprudence',
    title: 'Jurisprudence',
    excerpt: 'Les décisions de justice commentées, classées par thème.',
    content: 'La **jurisprudence** commentée : les décisions de justice qui font évoluer le droit de la copropriété, expliquées dans un langage accessible.\n\n:::bloc\n:::\n',
  },
  {
    slug: 'partenaires',
    title: 'Partenaires',
    excerpt: 'Les partenaires de l\'ACC Sud-Ouest.',
    content: 'L\'ACC Sud-Ouest travaille avec des **partenaires** sélectionnés pour leur compétence et leur connaissance de la copropriété.\n\n:::bloc\n:::\n\nVous souhaitez devenir partenaire ? Écrivez-nous à **contact@acc-sudouest.fr**.\n',
  },
  {
    slug: 'presse',
    title: 'Presse',
    excerpt: 'L\'ACC Sud-Ouest dans les médias : articles, parutions et mentions.',
    content: 'La presse régionale et spécialisée relaie régulièrement les positions de l\'**ACC Sud-Ouest** sur la copropriété. Cette page rassemble les articles, parutions et mentions qui nous concernent.\n\n:::bloc\n:::\n',
  },
  {
    slug: 'institutionnels',
    title: 'Institutionnels',
    excerpt: 'Les relations de l\'association avec les acteurs institutionnels.',
  },
  {
    // Créée d'abord comme page ordinaire, promue page système pour avoir son
    // entrée dans le menu d'administration (migration `page_outils_services`).
    slug: 'outils-et-services',
    title: 'Outils et services',
    excerpt: 'Les outils et services que l\'ACC Sud-Ouest met à disposition de ses adhérents.',
  },
  {
    slug: 'adherer',
    title: 'Adhérer',
    excerpt: 'Les formules d\'adhésion à l\'ACC Sud-Ouest et leurs avantages.',
  },
  {
    /*
     * Parente des pages de cible (`/qui-accompagnons-nous/<slug>`), une par
     * public de l'association. Celles-ci sont aussi des pages système, mais
     * elles ne figurent pas dans ce registre : elles naissent et meurent avec
     * leur cible, la table `cibles` fait foi (voir `server/utils/cibles.ts`).
     */
    slug: 'qui-accompagnons-nous',
    title: 'Qui accompagnons-nous ?',
    excerpt: 'Les publics que l\'ACC Sud-Ouest accompagne : conseils syndicaux, syndics bénévoles, copropriétaires…',
  },
  {
    slug: 'contact',
    title: 'Contact',
    excerpt: 'Contacter l\'ACC Sud-Ouest.',
    // Le formulaire et les coordonnées ne paraissent qu'où la brique est
    // posée. L'amorce la pose seule, sans texte : une page de contact sans
    // formulaire est bien plus gênante qu'une page de législation sans
    // mosaïque, et personne ne penserait à chercher une brique manquante.
    content: ':::bloc\n:::',
  },

  // ── Espace adhérent ──
  {
    slug: 'ateliers',
    title: 'Ateliers',
    excerpt: 'Les ateliers proposés aux adhérents, à venir et passés.',
    content: 'Les ateliers de l\'ACC Sud-Ouest réunissent copropriétaires et colotis autour des questions qui reviennent le plus souvent : **charges**, **assemblée générale**, **travaux**, rôle du conseil syndical. Chaque séance est animée par un intervenant du réseau et laisse une large place aux questions.\n\nLes séances à venir sont ouvertes à l\'inscription ; les précédentes restent consultables, avec leur rediffusion lorsqu\'elle est disponible.\n\n:::bloc\n:::\n\nUne question sur un atelier ? Écrivez-nous à **contact@acc-sudouest.fr** : nous répondons sous quarante-huit heures.\n',
  },
  {
    slug: 'lettres-mensuelles',
    title: 'Lettres mensuelles',
    excerpt: 'Les lettres mensuelles adressées aux adhérents.',
    content: 'Chaque mois, l\'ACC Sud-Ouest publie une lettre qui fait le point sur l\'**actualité de la copropriété** : évolutions réglementaires, décisions de justice marquantes, réponses aux questions les plus fréquentes de nos adhérents.\n\n:::bloc\n:::\n\nLes lettres des années précédentes restent consultables. Une question sur un numéro en particulier ? Écrivez-nous à **contact@acc-sudouest.fr**.\n',
  },
  {
    slug: 'librairie',
    title: 'Librairie',
    excerpt: 'Les documents mis à disposition des adhérents.',
    content: 'Notre **librairie** rassemble guides pratiques, modèles de documents et ouvrages de référence à destination des copropriétaires et des colotis.\n\n:::bloc\n:::\n',
  },
  {
    // Le formulaire suit la règle commune : il ne paraît qu'où la brique est
    // posée. L'amorce la pose donc seule, sans texte — ce que le rédacteur
    // veut dire avant ou après lui reste à écrire, mais l'accès à l'espace
    // adhérent ne doit pas attendre qu'il y pense.
    slug: 'connexion',
    title: 'Connexion',
    excerpt: 'Accès à l\'espace adhérent de l\'ACC Sud-Ouest.',
    content: ':::bloc\n:::',
  },
  {
    // Même parti que la connexion, dont cette page est le pendant.
    slug: 'mot-de-passe-oublie',
    title: 'Mot de passe oublié',
    excerpt: 'Réinitialisation du mot de passe de l\'espace adhérent.',
    content: ':::bloc\n:::',
  },

  // ── Pages légales ──
  {
    slug: 'mentions-legales',
    title: 'Mentions légales',
    excerpt: 'Mentions légales du site de l\'ACC Sud-Ouest.',
  },
  {
    slug: 'politique-de-confidentialite',
    title: 'Politique de confidentialité',
    excerpt: 'Traitement des données personnelles et usage des cookies.',
  },
]

/** Slugs des pages système, pour une vérification directe */
/**
 * Réglage de l'écran d'édition d'une page : celui de sa définition si c'est une
 * page système qui en déclare un, sinon l'écran complet.
 */
export function editionPage(slug: string, isSystem: boolean): Required<EditionPage> {
  const definition = isSystem ? systemPages.find(p => p.slug === slug)?.edition : undefined
  return {
    masquer: definition?.masquer ?? [],
    champsAvantExtrait: definition?.champsAvantExtrait ?? false,
  }
}

export const systemPageSlugs = new Set(systemPages.map(page => page.slug))

/**
 * Slug de la page d'accueil.
 *
 * Nommé plutôt que recopié : plusieurs endroits doivent la reconnaître — les
 * champs personnalisés qui ne s'affichent que sur elle, le site qui la sert à
 * `/`. Une chaîne écrite en dur à chacun se serait désynchronisée en silence.
 *
 * Le slug convient ici là où un identifiant ne conviendrait pas : il est le même
 * dans toutes les bases, quand l'identifiant diffère de l'une à l'autre. Le
 * serveur refuse par ailleurs de renommer une page système, si bien que ce
 * repère ne peut pas bouger.
 */
export const HOME_PAGE_SLUG = 'accueil'

/**
 * Pages dont l'adresse publique diffère de leur slug.
 *
 * Le slug est une **clé** : il relie la page au menu d'administration
 * (`/pages/<slug>`), au seed et au site. L'adresse publique, elle, est ce que le
 * visiteur tape. Les deux coïncident partout, sauf pour l'accueil : le site la
 * sert à `/` et jamais à `/accueil`, une même page à deux adresses créant du
 * contenu dupliqué aux yeux des moteurs.
 *
 * L'écran d'édition affichait donc une adresse fausse. Plutôt que de vider le
 * slug en base — ce qui aurait cassé les routes d'administration, le seed et le
 * lien avec le site, pour un gain nul — la correspondance est déclarée ici et
 * l'affichage seul s'y conforme.
 *
 * Ce n'est pas un cas particulier de l'accueil : toute page système que le site
 * déciderait de servir ailleurs s'ajoute ici, sans retoucher les composants.
 */
export const systemPagePublicPaths: Record<string, string> = {
  [HOME_PAGE_SLUG]: '/',
}

/**
 * Adresse publique d'une page, depuis son chemin complet.
 *
 * C'est l'adresse à **émettre** : celle qu'un lien, un résultat de recherche ou
 * le plan du site donnent au visiteur. Elle suit le chemin partout, sauf là où
 * `systemPagePublicPaths` en décide autrement.
 *
 * Le site ne sert plus l'accueil qu'à `/` : `/accueil` y répond 404
 * (ACCSudOuest/site#101). Composer `/${path}` à la main, c'est donc émettre un
 * lien mort pour cette page — ce que faisaient la recherche publique et le
 * sélecteur de liens (#272).
 *
 * Seules les pages système figurent dans la table, et leur slug leur est
 * réservé à la racine : un chemin qui s'y trouve ne peut désigner qu'elles.
 */
export function adressePubliquePage(path: string): string {
  return systemPagePublicPaths[path] ?? `/${path}`
}

/**
 * Adresse publique d'un résultat de recherche, quel que soit son type.
 *
 * Les deux recherches — publique et d'administration — lisent en SQL un chemin
 * déjà préfixé pour chaque type (`article/mon-titre`). Seule la page demande
 * une traduction, et la décider ici évite que chaque route la réécrive : la
 * première tentative comparait, dans l'une d'elles, le filtre de la requête
 * au lieu du type du résultat.
 */
export function adresseResultatRecherche(type: string, chemin: string): string {
  return type === 'page' ? adressePubliquePage(chemin) : `/${chemin}`
}

/**
 * Adresse publique d'une page, quand elle ne suit pas son slug.
 *
 * Rend `null` dans le cas ordinaire — le composant affiche alors le slug tel
 * qu'il est enregistré. Ne s'applique qu'aux pages système : le slug d'une page
 * ordinaire est modifiable, et lui superposer un affichage figé empêcherait de
 * le changer.
 */
export function cheminPublicSysteme(slug: string, isSystem: boolean): string | null {
  if (!isSystem) return null

  return systemPagePublicPaths[slug] ?? null
}

/**
 * Page dont chaque type de contenu reprend l'habillage.
 *
 * Deux réglages en dépendent, sans se confondre : le bandeau d'en-tête —
 * couleur, pictogramme, rotation — et le bandeau d'action de bas de page. Le
 * premier a nommé cette table, le second l'a rejointe, la parenté étant la
 * même dans les deux cas.
 *
 * **Une correspondance figée, non un lien de parenté.** Un article n'est pas
 * l'enfant de la page « Articles » — aucune colonne ne les relie, et rien dans
 * la base ne remonte de l'un à l'autre. C'est ici, en clair, qu'il est écrit
 * de quelle page il reprend son habillage.
 *
 * Un lien de parenté aurait supposé un arbre à parcourir, des cas où le parent
 * manque, et des règles de remontée à trancher. La correspondance ne pose
 * aucune de ces questions : elle vaut pour tous les contenus d'un type, sans
 * exception ni condition.
 *
 * La fiche de législation prend la page « Législation », non son thème. Le
 * thème porte bien une couleur et un pictogramme, mais ils habillent sa carte
 * dans la mosaïque : les faire descendre sur le bandeau de chaque fiche
 * mêlerait deux réglages que rien ne rend équivalents.
 *
 * Le titre et la description du bandeau restent propres au contenu : ils le
 * nomment, quand la couleur et le pictogramme situent la rubrique.
 *
 * Le nom dit « bannière » parce que l'en-tête est arrivé le premier ; le
 * renommer toucherait une vingtaine de routes sans rien clarifier de plus que
 * ce paragraphe.
 */
export const pageBanniereParContenu = {
  article: 'articles',
  news: 'actualites',
  jurisprudence: 'jurisprudence',
  legislation: 'legislation',
  legislationTheme: 'legislation',
  partner: 'partenaires',
  press: 'presse',
  workshop: 'ateliers',
  newsletter: 'lettres-mensuelles',
  library: 'librairie',
} as const

/** Type de contenu dont le bandeau suit une page. */
export type ContenuABanniereHeritee = keyof typeof pageBanniereParContenu

/**
 * Réglages de bandeau qu'un contenu hérite, ou remplace en propre.
 *
 * La rotation accompagne le pictogramme : elle le penche, et l'un sans l'autre
 * donnerait une icône héritée dans une inclinaison choisie ailleurs.
 */
export interface BanniereHeritable {
  headerIcon: string | null
  headerIconRotation: number | null
  headerColor: string | null
}

/**
 * Résout le bandeau d'un contenu : le sien s'il l'a personnalisé, celui de sa
 * page sinon.
 *
 * Le drapeau tranche seul, jamais la valeur des champs. `headerIconRotation` a
 * pour défaut `-30` en base et ne vaut donc jamais `NULL` : déduire l'héritage
 * de champs vides aurait confondu « je n'ai rien choisi » avec « j'ai choisi
 * -30 », et cette rotation-là est justement la plus courante.
 */
export function resoudreBanniere(
  contenu: BanniereHeritable & { headerCustom?: boolean | null },
  page: BanniereHeritable | null,
): BanniereHeritable {
  if (contenu.headerCustom) {
    return {
      headerIcon: contenu.headerIcon,
      headerIconRotation: contenu.headerIconRotation,
      headerColor: contenu.headerColor,
    }
  }

  /*
   * Page introuvable — jamais en fonctionnement normal, les pages système
   * étant amorcées au déploiement. Le contenu garde alors ses propres valeurs
   * plutôt que de paraître sans bandeau : mieux vaut un habillage inattendu
   * qu'une page nue.
   */
  if (!page) {
    return {
      headerIcon: contenu.headerIcon,
      headerIconRotation: contenu.headerIconRotation,
      headerColor: contenu.headerColor,
    }
  }

  return {
    headerIcon: page.headerIcon,
    headerIconRotation: page.headerIconRotation,
    headerColor: page.headerColor,
  }
}

/**
 * Référence de bandeau d'action qu'un contenu hérite, ou remplace en propre.
 *
 * Les deux champs voyagent ensemble : `footerCtaTemplateId` seul ne dit rien
 * sans le type qui le rend actif, et un type `template` sans identifiant ne
 * désigne aucun bandeau.
 */
export interface CtaHeritable {
  footerCtaType: string | null
  footerCtaTemplateId: number | null
}

/**
 * Résout la référence de bandeau d'action : celle du contenu s'il l'a
 * choisie, celle de sa page de rubrique sinon.
 *
 * Même forme que `resoudreBanniere`, et pour la même raison : le drapeau
 * tranche seul. `footerCtaType` a pour défaut `none` et ne vaut jamais `NULL`
 * — déduire l'héritage d'un type `none` aurait confondu « je ne veux aucun
 * bandeau » avec « je n'ai rien choisi », et un contenu qui refuse un bandeau
 * se serait vu imposer celui de sa rubrique.
 *
 * La résolution s'arrête à la **référence**. Ce qu'elle désigne — le bandeau
 * lui-même, ses couleurs, ses boutons — est l'affaire de `resolveCta()`, qui
 * écarte au passage un bandeau désactivé : un contenu qui hérite d'une page
 * pointant un bandeau retiré n'affiche donc rien, comme la page elle-même.
 */
export function resoudreReferenceCta(
  contenu: CtaHeritable & { footerCtaCustom?: boolean | null },
  page: CtaHeritable | null,
): CtaHeritable {
  if (contenu.footerCtaCustom) {
    return {
      footerCtaType: contenu.footerCtaType,
      footerCtaTemplateId: contenu.footerCtaTemplateId,
    }
  }

  /*
   * Page introuvable — jamais en fonctionnement normal, les pages système
   * étant amorcées au déploiement. Le contenu garde alors sa propre référence,
   * par symétrie avec le bandeau d'en-tête ; en pratique elle vaut `none`,
   * puisqu'un contenu qui en avait choisi une porte le drapeau.
   */
  if (!page) {
    return {
      footerCtaType: contenu.footerCtaType,
      footerCtaTemplateId: contenu.footerCtaTemplateId,
    }
  }

  return {
    footerCtaType: page.footerCtaType,
    footerCtaTemplateId: page.footerCtaTemplateId,
  }
}

/**
 * Rubrique d'administration à laquelle une page système sert de vitrine.
 *
 * Les pages piliers ne sont pas toutes de même nature. Les unes se suffisent —
 * « Qui sommes-nous », « Contact », les mentions légales : leur contenu *est*
 * la page. Les autres ne font qu'introduire une liste tenue ailleurs : la page
 * « Actualités » présente la rubrique, les actualités elles-mêmes se gèrent
 * dans `/actualites`.
 *
 * Toutes affichent « <titre> › Page » — la forme ne se discute pas ici. Cette
 * table dit seulement lesquelles ont une liste vers où pointer : le premier
 * maillon y mène, quand celui de « Qui sommes-nous » reste inerte faute d'avoir
 * autre chose derrière lui que la page même que l'on édite.
 *
 * Le libellé reprend celui du menu plutôt que le titre de la page : c'est le
 * nom de la rubrique, et il ne bouge pas quand un rédacteur renomme la vitrine.
 */
export const rubriqueParPageSysteme: Record<string, { label: string, to: string }> = {
  articles: { label: 'Articles', to: '/articles' },
  actualites: { label: 'Actualités', to: '/actualites' },
  legislation: { label: 'Législation', to: '/legislation' },
  jurisprudence: { label: 'Jurisprudence', to: '/jurisprudence' },
  partenaires: { label: 'Partenaires', to: '/partenaires' },
  presse: { label: 'Presse', to: '/presse' },
  ateliers: { label: 'Ateliers', to: '/ateliers' },
  'lettres-mensuelles': { label: 'Lettres mensuelles', to: '/lettres-mensuelles' },
  librairie: { label: 'Librairie', to: '/librairie' },
}

/**
 * La page alimente-t-elle une valeur que le site affiche **hors d'elle-même** ?
 *
 * Une page de rubrique porte l'icône d'en-tête de son type de contenu, servie
 * par `/api/public/content-types` et affichée sur chaque ligne des listes qui
 * mêlent les types — page de mot-clé, page de catégorie, recherche.
 *
 * La distinction commande le **périmètre de purge**, et rien d'autre. Le site
 * met ses appels d'API en cache sous `nitro:handlers`, que seul `scope: 'menus'`
 * balaie ; `scope: 'page'` ne touche que `nitro:routes`, et pour la seule route
 * de la page. Publier une nouvelle icône de « Jurisprudence » sans le périmètre
 * large laissait donc les listes afficher l'ancienne, indéfiniment et sans
 * qu'aucune erreur ne soit levée — le bandeau de la rubrique disant une chose,
 * les listes une autre.
 *
 * **Pourquoi ne pas purger large sur toute page système.** « Qui sommes-nous »,
 * les mentions légales et la page de connexion n'alimentent aucune liste :
 * balayer `nitro:handlers` à leur enregistrement ferait retomber le site entier
 * sur le back-office sans rien rafraîchir de plus.
 *
 * **Pourquoi `rubriqueParPageSysteme` fait le critère.** C'est déjà elle qui dit
 * lesquelles des seize pages piliers sont la vitrine d'une rubrique. Une
 * seconde liste aurait pu en oublier une, et l'oubli d'une seule rubrique est
 * exactement le défaut à éviter : cinq icônes suivraient, la sixième non.
 */
export function alimenteListesMelangees(slug: string, isSystem: boolean): boolean {
  if (!isSystem) return false

  return slug in rubriqueParPageSysteme
}

/**
 * Pages dont le site attend la brique « bloc de la page » (`:::bloc`).
 *
 * Ces rubriques mêlent deux natures : du texte rédigé ici, et un bloc que seul
 * le site sait produire — la mosaïque des thèmes, le formulaire de contact, la
 * liste des articles. La brique marque **où** ce bloc s'insère dans le texte.
 *
 * **Sans brique, le bloc ne paraît pas.** Le site ne le monte que là où le
 * rédacteur l'a posée, et n'affiche rien à la place : le message « Aucun
 * élément publié » est lui-même à l'intérieur du bloc. Une rubrique dont la
 * brique a été retirée sort donc avec son bandeau, son texte, son pied de
 * page — et rien entre les deux.
 *
 * L'écran d'édition s'en sert pour prévenir le rédacteur. Il prévient et n'en
 * fait pas plus : le texte reste enregistrable sans brique, une rubrique
 * pouvant légitimement s'en passer le temps d'une refonte, ou renvoyer un jour
 * vers un service extérieur — un formulaire de contact hébergé ailleurs, par
 * exemple. Poser la brique reste un geste d'éditeur, jamais une correction
 * automatique.
 *
 * **Une liste tenue à la main, et c'est voulu.** La déduire des amorces
 * `content` aurait confondu deux choses distinctes : ce que le seed pose au
 * premier jour, et ce que le site exige en permanence. Une rubrique dont
 * l'amorce serait un jour vidée aurait alors cessé d'être surveillée, en
 * silence — l'écran cessant d'avertir précisément quand la page en aurait le
 * plus besoin.
 *
 * **Elle double la liste du site**, qui vit dans un dépôt séparé : ce sont les
 * pages appelant `scinderContenu`. Rien ne peut dériver l'une de l'autre. Une
 * rubrique à laquelle le site ajoute un bloc natif s'ajoute donc ici aussi,
 * faute de quoi l'écran laissera passer une page amputée sans rien dire.
 */
export const systemPagesAvecBlocNatif = new Set([
  'articles',
  'actualites',
  'legislation',
  'jurisprudence',
  'partenaires',
  'presse',
  'contact',
  'ateliers',
  'lettres-mensuelles',
  'librairie',
  'connexion',
  'mot-de-passe-oublie',
])

/**
 * Pages piliers que l'on ne peut pas réserver aux adhérents.
 *
 * La visibilité reste libre sur la plupart des pages du site : la restreindre
 * n'en retire aucune, elle demande seulement une session. Sur celles-ci, cette
 * session est précisément ce que le visiteur n'a pas — ou ce que la loi
 * interdit d'exiger de lui.
 *
 * Trois raisons distinctes, qu'il vaut mieux ne pas confondre :
 *
 * - **On ne peut pas s'y connecter depuis l'intérieur.** `connexion` et
 *   `mot-de-passe-oublie` sont le chemin vers la session ; les réserver
 *   demanderait la session pour l'obtenir. `accueil` est la porte d'entrée du
 *   site : fermée, il n'existe plus rien à ouvrir derrière.
 * - **Elles s'adressent à qui n'est pas encore adhérent.** `adherer` porte
 *   l'argument d'adhésion, `contact` la façon de nous joindre, `qui-sommes-nous`,
 *   `institutionnels` et `fnacc` la présentation de l'association et de sa
 *   fédération. Les réserver reviendrait à ne les montrer qu'à ceux qu'elles
 *   n'ont plus à convaincre.
 * - **La loi les veut accessibles.** `mentions-legales` et
 *   `politique-de-confidentialite` doivent pouvoir être lues sans compte : c'est
 *   la condition même de leur existence, un traitement de données ne s'annonçant
 *   pas derrière une porte fermée.
 *
 * **Les rubriques éditoriales n'y figurent pas.** Ateliers, lettres mensuelles,
 * librairie sont réservées aujourd'hui ; articles, actualités, législation,
 * jurisprudence, partenaires et presse peuvent l'être demain. Ouvrir ou fermer
 * une rubrique est une décision d'association, et elle se prend depuis
 * l'administration.
 *
 * **La coupure `<!--adherents-->` reste entière sur ces pages.** Ce sont deux
 * réserves indépendantes, que `servirContenu` traite séparément : la visibilité
 * retient la page entière, la coupure n'en retranche que la fin. Une page
 * verrouillée ici garde donc le droit de réserver son dernier paragraphe —
 * `adherer` le fait déjà, et doit continuer.
 *
 * Posée côté écran pour griser le champ, et côté serveur pour le refuser : un
 * champ désactivé ne protège rien d'un appel direct à l'API.
 *
 * **Chaque page porte son motif, et non un drapeau.** L'administrateur qui
 * trouve un champ fermé doit lire la raison de cette page-là : « on y va pour se
 * connecter » n'explique pas les mentions légales, et une phrase commune assez
 * large pour couvrir les dix n'aurait plus rien appris à personne. Le motif sert
 * aussi bien à l'écran qu'au message d'erreur du serveur — la même phrase, quel
 * que soit le chemin par lequel on a tenté de fermer la page.
 */
export const motifPageToujoursPublique: Record<string, string> = {
  'accueil': 'C\'est la porte d\'entrée du site : la réserver le rendrait inaccessible.',
  'connexion': 'On vient ici pour se connecter : la réserver demanderait d\'être déjà connecté.',
  'mot-de-passe-oublie': 'C\'est le recours de celui qui ne peut plus se connecter : elle doit rester ouverte.',
  'adherer': 'Elle s\'adresse à ceux qui ne sont pas encore adhérents : la réserver la montrerait aux seuls adhérents.',
  'contact': 'Une page de contact qui demanderait un compte refuserait justement qu\'on la contacte.',
  'mentions-legales': 'La loi impose qu\'elles soient accessibles à tous, sans compte.',
  'politique-de-confidentialite': 'Le RGPD impose qu\'elle soit accessible à tous, sans compte.',
  'qui-sommes-nous': 'Elle présente l\'association à ceux qui ne la connaissent pas encore.',
  'institutionnels': 'Page institutionnelle : elle s\'adresse aussi à des interlocuteurs extérieurs.',
  'fnacc': 'Elle présente la fédération, y compris à ceux qui n\'ont pas de compte.',
}

/** Slugs dont la visibilité est figée, pour une vérification directe. */
export const systemPagesToujoursPubliques = new Set(Object.keys(motifPageToujoursPublique))

/**
 * Pourquoi cette page ne peut pas être réservée — `null` si elle le peut.
 *
 * Rend le motif plutôt qu'un booléen : l'appelant qui veut seulement savoir si
 * le verrou s'applique teste la nullité, quand celui qui doit l'expliquer a
 * déjà la phrase sous la main.
 *
 * Réservé aux pages système : une page libre créée sous l'un de ces slugs — la
 * base ne l'interdit pas hors de la racine — reste modifiable, n'étant ni servie
 * au même endroit ni pointée par le menu.
 */
export function motifVisibiliteFigee(slug: string, isSystem: boolean): string | null {
  if (!isSystem) return null

  return motifPageToujoursPublique[slug] ?? null
}

/**
 * La page attend-elle un bloc natif que sa brique commande ?
 *
 * Réservé aux pages système : une page libre passe par le rendu générique, qui
 * n'a pas de bloc à monter. Une brique posée sur l'une d'elles ne fait que
 * couper son texte en deux, sans rien insérer.
 *
 * **Les pages de cible l'attendent toutes** (bloc « Accès aux ressources ») :
 * elles ne figurent pas dans `systemPagesAvecBlocNatif`, qui est figée, mais
 * se reconnaissent à la table `cibles` — l'appelant le dit par `estPageCible`,
 * vrai quand la réponse de la page porte sa `cible`. Une cible ajoutée depuis
 * l'écran est donc surveillée sans toucher au code.
 */
export function attendBlocNatif(slug: string, isSystem: boolean, estPageCible = false): boolean {
  if (!isSystem) return false
  if (estPageCible) return true

  return systemPagesAvecBlocNatif.has(slug)
}

/*
 * La brique, telle que l'éditeur l'écrit et que le site la relit.
 *
 * Même critère que leurs deux analyseurs de directives (`^:::([a-z]+)$`) : la
 * brique tient sa ligne entière. Chercher la chaîne n'importe où aurait pris
 * pour une brique le `:::bloc` cité dans un exemple ou dans un bloc de code, et
 * l'écran se serait tu sur une page réellement amputée.
 */
const BRIQUE_BLOC = /^:::bloc\s*$/m

/**
 * Le contenu porte-t-il la brique du bloc natif ?
 *
 * Ne juge que sa présence : ni sa fermeture, ni son unicité, ni sa place dans
 * le texte. L'avertissement porte sur l'oubli pur et simple — le seul cas
 * observé — et le site reste juge du rendu.
 *
 * Une brique citée dans un bloc de code compte ici, quand le site la laisse en
 * texte : il découpe le HTML déjà rendu, là où cette lecture porte sur le
 * Markdown brut. L'écart demanderait un rendu complet pour un cas qui ne se
 * présente sur aucune rubrique surveillée, et il penche du bon côté — se taire
 * à tort sur une page de documentation vaut mieux qu'alerter à tort sur les
 * neuf pages où la brique est bien posée.
 */
export function contientBriqueBloc(contenu: string | null | undefined): boolean {
  if (!contenu) return false

  return BRIQUE_BLOC.test(contenu)
}
